import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useOpenRequestsCount } from "../../hooks/useOpenRequestsCount";
import { useOpenOrdersCount } from "../../hooks/useOpenOrdersCount";
import { Logo } from "../ui/Logo";
import { UcpButton } from "./UcpButton";
import { NAV_ITEMS, SETTINGS_ITEM } from "./navItems";

/** Ausklappbare Navigation für Tablet/Smartphone (unterhalb der md-Breakpoint). */
export function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, hasPermission, isNavItemHidden } = useAuth();
  const openRequestsCount = useOpenRequestsCount();
  const openOrdersCount = useOpenOrdersCount();
  const badgeCounts = { openRequests: openRequestsCount, openOrders: openOrdersCount };
  if (!open) return null;
  const visible = NAV_ITEMS.filter((item) => hasPermission(item.permission) && !isNavItemHidden(item.to));

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="absolute left-0 top-0 bottom-0 w-64 bg-surface-card bg-carbon border-r border-surface-border flex flex-col">
        <div className="h-16 flex items-center gap-2.5 px-4 border-b border-surface-border bg-black/20">
          <Logo className="h-10 w-auto shrink-0" />
          <div className="leading-tight min-w-0">
            <p className="font-display text-sm font-bold text-gray-100 uppercase tracking-wide truncate">Phantom of Hell</p>
            <p className="text-[10px] text-sand-muted uppercase tracking-[0.25em]">LBV-System</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={onClose}
              className={({ isActive }) =>
                // "Dashboard" ist immer der erste Eintrag und erhält Abstand
                // NACH sich, damit er klar von den übrigen Menüpunkten
                // abgesetzt ist - bewusst 20% mehr als der sonst übliche
                // "mt-3"/"mb-3"-Abstand (0.75rem), also 0.9rem.
                `flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  item.to === "/" ? "mb-[0.9rem]" : ""
                } ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.badgeKey && badgeCounts[item.badgeKey] > 0 && (
                <span className="min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-accent text-surface text-[10px] font-display font-bold text-center leading-none">
                  {badgeCounts[item.badgeKey]}
                </span>
              )}
            </NavLink>
          ))}

          {/* "Lagersystem": bewusst NICHT Teil von NAV_ITEMS - eigene
              Berechtigung "discord_sync.view" (siehe Migration 0031), ab Werk
              President/VizePresident/Secretary/Treasurer/Phantom. Der
              Systemadministrator hat unabhängig davon weiterhin Zugriff. */}
          {hasPermission("discord_sync.view") && !isNavItemHidden("/warehouse-sync") && (
            <NavLink
              to="/warehouse-sync"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">🔄</span>
              <span className="flex-1">Lagersystem</span>
            </NavLink>
          )}

          {/* "Kassensystem": bewusst NICHT Teil von NAV_ITEMS - eigene
              Berechtigung "kassensystem.view" (siehe Migration 0036), ab Werk
              President/VizePresident/Secretary/Treasurer/Phantom. Der
              Systemadministrator hat unabhängig davon weiterhin Zugriff. */}
          {hasPermission("kassensystem.view") && !isNavItemHidden("/kassensystem") && (
            <NavLink
              to="/kassensystem"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">💵</span>
              <span className="flex-1">Kassensystem</span>
            </NavLink>
          )}

          {/* "Sanktionssystem": bewusst NICHT Teil von NAV_ITEMS - eigene
              Berechtigung "sanctionssystem.view" (siehe Migration 0044), ab
              Werk President/VizePresident/Secretary/Treasurer/Phantom. Der
              Systemadministrator hat unabhängig davon weiterhin Zugriff. */}
          {hasPermission("sanctionssystem.view") && !isNavItemHidden("/sanctionssystem") && (
            <NavLink
              to="/sanctionssystem"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">🚨</span>
              <span className="flex-1">Sanktionssystem</span>
            </NavLink>
          )}

          {/* "Benzin-Route" (vormals "Unsere Route"): bewusst NICHT Teil von
              NAV_ITEMS - eigene Berechtigung "route.view" (siehe Migration
              0045), ab Werk JEDER regulären Rolle zugewiesen. Der
              Systemadministrator hat unabhängig davon weiterhin Zugriff. */}
          {hasPermission("route.view") && !isNavItemHidden("/route") && (
            <NavLink
              to="/route"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">🗺️</span>
              <span className="flex-1">Benzin-Route</span>
            </NavLink>
          )}

          {/* "Routenregeln": bewusst NICHT Teil von NAV_ITEMS - eigene
              Berechtigung "routenregeln.view" (siehe Migration 0053), ab
              Werk allen regulären Rollen + "Routenpartner" zugewiesen. Der
              Systemadministrator hat unabhängig davon weiterhin Zugriff. */}
          {hasPermission("routenregeln.view") && !isNavItemHidden("/route-rules") && (
            <NavLink
              to="/route-rules"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">📖</span>
              <span className="flex-1">Routenregeln</span>
            </NavLink>
          )}

          {/* "Nachrichten": bewusst NICHT Teil von NAV_ITEMS - eigene
              Berechtigung "messages.manage" (siehe Migration 0054), ab Werk
              President/VizePresident/Phantom. Der Systemadministrator hat
              unabhängig davon weiterhin Zugriff. */}
          {hasPermission("messages.manage") && !isNavItemHidden("/messages") && (
            <NavLink
              to="/messages"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">✉️</span>
              <span className="flex-1">Nachrichten</span>
            </NavLink>
          )}

          {/* "Blacklist": bewusst NICHT Teil von NAV_ITEMS - eigene
              Berechtigung "blacklist.manage" (siehe Migration 0055), ab
              Werk allen aktuell existierenden regulären Rollen außer
              "Routenpartner" zugewiesen. Der Systemadministrator hat
              unabhängig davon weiterhin Zugriff. */}
          {hasPermission("blacklist.manage") && !isNavItemHidden("/blacklist") && (
            <NavLink
              to="/blacklist"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">🚫</span>
              <span className="flex-1">Blacklist</span>
            </NavLink>
          )}

          {/* "Preisliste": bewusst NICHT Teil von NAV_ITEMS - eigene
              Berechtigung "price_list.view" (siehe Migration 0048), ab Werk
              ALLEN sieben regulären Rollen zugewiesen (inkl. "Member" -
              sichtbar, aber ohne "price_list.manage" nicht bearbeitbar/
              löschbar, siehe PriceListPage.tsx). Der Systemadministrator hat
              unabhängig davon weiterhin Zugriff. */}
          {hasPermission("price_list.view") && !isNavItemHidden("/price-list") && (
            <NavLink
              to="/price-list"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">🏷️</span>
              <span className="flex-1">Preisliste</span>
            </NavLink>
          )}

          {/* "Das Ende": bewusst NICHT Teil von NAV_ITEMS/der konfigurierbaren
              Rechteverwaltung - ausschließlich für den Systemadministrator. */}
          {user?.isSuperAdmin && !isNavItemHidden("/das-ende") && (
            <NavLink
              to="/das-ende"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-display font-bold uppercase tracking-wider border-2 ${
                  isActive
                    ? "bg-danger text-white border-danger"
                    : "bg-danger-muted text-danger border-danger/70 hover:bg-danger hover:text-white"
                }`
              }
            >
              <span className="text-base">💀</span>
              <span className="flex-1">Das Ende</span>
              <span className="text-base">⚠️</span>
            </NavLink>
          )}

          {/* "Passwort ändern": bewusst NICHT Teil von NAV_ITEMS/der
              konfigurierbaren Rechteverwaltung - FÜR JEDEN eingeloggten
              Benutzer sicht- und erreichbar, unabhängig von Rolle oder
              Berechtigungen (keine hasPermission()-Prüfung). Bewusst der
              WIRKLICH letzte Reiter direkt vor "Einstellungen" - dadurch bei
              jedem Benutzer, unabhängig von dessen freigeschalteten
              Reitern, an derselben Stelle auffindbar. Auch dieser Reiter ist
              (anders als "Einstellungen") über die persönliche Präferenz
              "Reiter ausblenden" ausblendbar. */}
          {!isNavItemHidden("/change-password") && (
            <NavLink
              to="/change-password"
              onClick={onClose}
              className={({ isActive }) =>
                `mt-3 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                  isActive
                    ? "bg-accent-muted text-gray-100 border-accent"
                    : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
                }`
              }
            >
              <span className="text-base">🔑</span>
              <span className="flex-1">Passwort ändern</span>
            </NavLink>
          )}

          {/* "Einstellungen": bewusst NICHT Teil von NAV_ITEMS/dem obigen
              map() - muss der WIRKLICH letzte Eintrag im gesamten Menü sein
              (siehe navItems.ts). Der Abstand zu "Passwort ändern" ist
              bewusst 20% größer als der sonst übliche "mt-3"-Abstand
              (0.75rem), also 0.9rem. "!mt-[0.9rem]" (wichtig): das
              übergeordnete <nav> setzt "space-y-0.5", was per Geschwister-
              Selektor eine höhere Spezifität als ein einfaches
              ".mt-[0.9rem]" hat und den Abstand sonst wieder überschreiben
              würde. Bewusst OHNE hasPermission()- UND OHNE
              isNavItemHidden()-Prüfung - für JEDEN eingeloggten Benutzer
              immer sicht- und erreichbar, und als einziger Reiter NICHT
              über "Reiter ausblenden" ausblendbar. */}
          <NavLink
            to={SETTINGS_ITEM.to}
            onClick={onClose}
            className={({ isActive }) =>
              `!mt-[0.9rem] flex items-center gap-3 px-3 py-2 rounded text-sm font-medium tracking-wide border-l-2 ${
                isActive
                  ? "bg-accent-muted text-gray-100 border-accent"
                  : "text-gray-400 border-transparent hover:bg-surface-raised hover:text-sand"
              }`
            }
          >
            <span className="text-base">{SETTINGS_ITEM.icon}</span>
            <span className="flex-1">{SETTINGS_ITEM.label}</span>
          </NavLink>
        </nav>

        {/* Fest unten in der Navigation verankert, direkt unter "Das Ende" -
            siehe UcpButton.tsx sowie .ucp-neon-btn in styles/index.css. */}
        <div className="p-3 border-t border-surface-border/60 shrink-0">
          <UcpButton />
        </div>
      </div>
    </div>
  );
}
